/**
 * Determines the next adaptive diagnostic question.
 *
 * @returns {string|null} next questionId or null if diagnostic complete
 */
export function nextAdaptiveQuestion({
  definition,
  sequence,
  responses,
}) {
  if (!definition?.subtopics || !Array.isArray(definition.subtopics)) {
    throw new Error("Invalid diagnostic definition");
  }

  // Ensure subtopics are in order
  const subtopics = [...definition.subtopics].sort(
    (a, b) => a.order - b.order
  );

  // Helper to check if a question is in a subtopic
  const subtopicHasQuestion = (sub, qid) =>
    Object.values(sub.questions).some((arr) => arr.includes(qid));

  // Determine current subtopic index
  let currentSubtopicIndex = 0;

  if (sequence.length > 0) {
    const lastQ = sequence[sequence.length - 1];

    const foundIndex = subtopics.findIndex((sub) =>
      subtopicHasQuestion(sub, lastQ)
    );

    if (foundIndex !== -1) {
      currentSubtopicIndex = foundIndex;
    }
  }

  // Walk subtopics until we find the next question
  for (
    let i = currentSubtopicIndex;
    i < subtopics.length;
    i++
  ) {
    const sub = subtopics[i];
    const { questions } = sub;

    const level1 = questions["1"];
    const level2 = questions["2"];
    const level3 = questions["3"];

    const allQs = [
      ...level2,
      ...level1,
      ...level3,
    ];

    const answered = sequence.filter((q) => allQs.includes(q));

    // ---- Question 1: always 2a
    if (answered.length === 0) {
      return level2[0]; // 2a
    }

    // ---- Question 2: 1a or 3a
    if (answered.length === 1) {
      const firstQ = answered[0];
      const correct = responses[firstQ]?.correct;

      if (correct === true) return level3[0]; // 3a
      if (correct === false) return level1[0]; // 1a

      return null;
    }

    // ---- Question 3: b-level question
    if (answered.length === 2) {
      const secondQ = answered[1];
      const correct = responses[secondQ]?.correct;

      if (correct === true) {
        if (level3.includes(secondQ)) return level3[1]; // 3b
        return level2[1]; // 2b
      }

      if (correct === false) {
        if (level1.includes(secondQ)) return level1[1]; // 1b
        return level2[1]; // 2b
      }

      return null;
    }

    // ---- answered.length === 3 → block complete
    // move to next subtopic
  }

  // All subtopics exhausted
  return null;
}
